/***
 *
 *   EDD CASE (feature 004)
 *   Reviewer workflow for a single Enhanced Due Diligence case. Drives the
 *   state machine the backend exposes:
 *     awaiting_customer  -> waiting on the customer's submission
 *     awaiting_assessment-> complete the internal EDD assessment (editable,
 *                           scoring-capable form) -> POST .../assessment
 *     awaiting_approval  -> officer approve/decline + rationale -> POST .../approval
 *     closed_*           -> outcome
 *   EDD work is gated to EDD-capable assessors (FR-016); the server enforces
 *   it, `can_assess` only drives what the UI offers.
 *
 *   GET  /api/edd/:id                -> { case, history, assessment_form,
 *                                         can_assess, verification }
 *   POST /api/edd/:id/assessment     (answers)
 *   POST /api/edd/:id/approval       { decision, rationale, approver_role }
 *
 **********/

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Axios from "axios";
import { scrubSurveyImages } from "components/survey/formTheme";
import { useParams } from "react-router-dom";
import {
  Button,
  Card,
  Loader,
  Message,
  SubNav,
  ViewContext,
} from "components/lib";
import { StylesManager, Model } from "survey-core";
import { Survey } from "survey-react-ui";
import "survey-core/defaultV2.min.css";
import { applyStackgoTheme } from "components/survey/stackgo-theme";
import { attachScoring } from "./scoringRunner";
import { ScoringSummary } from "./scoringSummary";

const Settings = require("settings.json");
const serverUrl = Settings[process.env.NODE_ENV].server_url;

StylesManager.applyTheme("defaultV2");

const STATE_LABELS = {
  awaiting_customer: "Awaiting customer",
  awaiting_assessment: "Awaiting assessment",
  awaiting_approval: "Awaiting approval",
  closed_approved: "Closed — approved",
  closed_declined: "Closed — declined",
};

function buildSurveyModel(schema) {
  if (!schema || typeof schema !== "object") return null;
  const model = scrubSurveyImages(new Model(schema));
  applyStackgoTheme(model);
  model.showCompletedPage = false;
  return model;
}

function isFileLikeObject(value) {
  if (!value || typeof value !== "object") return false;
  return (
    (typeof value.name === "string" || typeof value.filename === "string") &&
    (typeof value.key === "string" ||
      typeof value.content === "string" ||
      typeof value.data === "string")
  );
}

// Resolve uploaded-file references in stored answers into downloadable
// blobs/urls so SurveyJS display mode can render them (mirrors the
// risk-assessment read path). Best-effort: an un-fetchable file keeps its
// reference rather than failing the whole submission.
async function hydrateAnswers(answers) {
  const cache = new Map();
  const hydrateNode = async (node) => {
    if (!node) return node;
    if (Array.isArray(node)) {
      const out = [];
      for (const item of node) out.push(await hydrateNode(item));
      return out;
    }
    if (isFileLikeObject(node)) {
      if (!node.key) return node;
      if (cache.has(node.key)) return { ...node, ...cache.get(node.key) };
      try {
        const response = await Axios.get("/api/utility/gcp/download", {
          params: {
            content: node.key,
            name: node.name || node.filename || node.key,
          },
        });
        const hydratedFile = {
          content: response.data?.blob,
          url: response.data?.url,
        };
        cache.set(node.key, hydratedFile);
        return { ...node, ...hydratedFile };
      } catch (err) {
        return node;
      }
    }
    if (typeof node === "object") {
      const out = {};
      for (const [key, value] of Object.entries(node)) {
        out[key] = await hydrateNode(value);
      }
      return out;
    }
    return node;
  };
  return hydrateNode(answers || {});
}

function formatSubmittedAnswers(sender, uploadPrefix) {
  const output = {};
  Object.keys(sender?.data || {}).forEach((key) => {
    const question = sender.getQuestionByName(key);
    if (!question) return;

    if (question.getType() === "file" && Array.isArray(question.value)) {
      output[key] = question.value.map((fileItem) => ({
        ...fileItem,
        key: fileItem.key || `${uploadPrefix}/${key}/${fileItem.name}`,
      }));
      return;
    }

    output[key] = question.value;
  });

  return output;
}

function ReviewHistory({ history }) {
  if (!Array.isArray(history) || !history.length) return null;

  return (
    <div className="space-y-2 pt-2">
      <p className="text-sm font-semibold text-slate-900">Decision history</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-slate-700">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="py-1 pr-2 font-medium">Event</th>
              <th className="py-1 pr-2 font-medium">Status</th>
              <th className="py-1 pr-2 font-medium">Rating</th>
              <th className="py-1 font-medium">When</th>
            </tr>
          </thead>
          <tbody>
            {history.map((event) => (
              <tr key={event.id} className="border-t border-slate-200">
                <td className="py-1 pr-2">{event.event_type || "-"}</td>
                <td className="py-1 pr-2">{event.status || "-"}</td>
                <td className="py-1 pr-2">{event.rating || "-"}</td>
                <td className="py-1">
                  {event.created_at
                    ? new Date(event.created_at).toLocaleString()
                    : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ApprovalForm({ caseId, onDone }) {
  const context = useContext(ViewContext);
  const [decision, setDecision] = useState("");
  const [rationale, setRationale] = useState("");
  const [role, setRole] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (submitting) return;
    if (!decision) {
      setError("Select approve or decline.");
      return;
    }
    if (!rationale.trim()) {
      setError("A rationale is required.");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await Axios.post(`/api/edd/${caseId}/approval`, {
        decision,
        rationale: rationale.trim(),
        approver_role: role.trim() || null,
      });
      context?.notification?.show(
        decision === "approve" ? "EDD case approved." : "EDD case declined.",
        "success",
        true
      );
      if (typeof onDone === "function") onDone();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Unable to record the approval decision right now."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3 rounded-panel border border-sg-line bg-white p-4">
      <p className="text-sm font-semibold text-slate-900">Officer decision</p>

      <label className="block text-sm text-slate-700">
        <span className="mb-1 block font-medium">Decision</span>
        <select
          value={decision}
          onChange={(event) => setDecision(event.target.value)}
          className="w-full max-w-xs rounded border border-slate-300 bg-white px-3 py-2 text-sm"
        >
          <option value="">Select decision…</option>
          <option value="approve">Approve</option>
          <option value="decline">Decline</option>
        </select>
      </label>

      <label className="block text-sm text-slate-700">
        <span className="mb-1 block font-medium">Approver role (optional)</span>
        <input
          value={role}
          onChange={(event) => setRole(event.target.value)}
          className="w-full max-w-xs rounded border border-slate-300 px-3 py-2 text-sm"
          placeholder="e.g. Compliance Officer"
        />
      </label>

      <label className="block text-sm text-slate-700">
        <span className="mb-1 block font-medium">Rationale (required)</span>
        <textarea
          value={rationale}
          onChange={(event) => setRationale(event.target.value)}
          rows={3}
          className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
          placeholder="Record the basis for this decision."
        />
      </label>

      {error && <p className="text-sm text-sg-red">{error}</p>}

      <Button
        text={submitting ? "Saving..." : "Record decision"}
        action={submit}
      />
    </div>
  );
}

export function EddCaseView() {
  const params = useParams();
  const context = useContext(ViewContext);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const isSubmittingRef = useRef(false);

  const fetchCase = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await Axios.get(`/api/edd/${params.id}`);
      setData(response.data?.data || null);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Unable to load this EDD case right now."
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [params.id]);

  useEffect(() => {
    fetchCase();
  }, [fetchCase]);

  const caseRecord = data?.case || null;
  const state = caseRecord?.state || null;
  const canAssess = data?.can_assess === true;
  const assessmentSchema = data?.assessment_form?.schema || null;
  const clientSubmission = data?.client_submission || null;
  const assessmentSubmission = data?.assessment_submission || null;
  const verification = data?.verification || null;

  // Hydrate any uploaded-file answers so the read-only forms can render the
  // attachments (documents are the substance of an EDD client submission).
  const [displayClientAnswers, setDisplayClientAnswers] = useState(null);
  const [displayAssessmentAnswers, setDisplayAssessmentAnswers] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const hydratedClient = clientSubmission?.answers
        ? await hydrateAnswers(clientSubmission.answers)
        : null;
      if (mounted) setDisplayClientAnswers(hydratedClient);
      const hydratedAssessment = assessmentSubmission?.answers
        ? await hydrateAnswers(assessmentSubmission.answers)
        : null;
      if (mounted) setDisplayAssessmentAnswers(hydratedAssessment);
    })();
    return () => {
      mounted = false;
    };
  }, [clientSubmission, assessmentSubmission]);

  // The customer's submitted client EDD form, rendered read-only so the assessor
  // can review it while completing the internal assessment. Answers fall back to
  // the raw payload until file hydration resolves.
  const clientModel = useMemo(() => {
    if (!clientSubmission?.schema) return null;
    const model = buildSurveyModel(clientSubmission.schema);
    if (!model) return null;
    model.mode = "display";
    model.data = displayClientAnswers || clientSubmission.answers || {};
    return model;
  }, [clientSubmission, displayClientAnswers]);

  // The completed internal EDD assessment, read-only — kept visible through
  // approval and after the case closes (the editable form below is only for
  // the awaiting_assessment step).
  const assessmentDisplayModel = useMemo(() => {
    if (!assessmentSubmission?.schema) return null;
    const model = buildSurveyModel(assessmentSubmission.schema);
    if (!model) return null;
    model.mode = "display";
    model.data =
      displayAssessmentAnswers || assessmentSubmission.answers || {};
    return model;
  }, [assessmentSubmission, displayAssessmentAnswers]);

  // latest completed EDD assessment event (carries the scoring summary)
  const assessmentEvent = useMemo(() => {
    const history = Array.isArray(data?.history) ? data.history : [];
    return (
      history.find(
        (event) =>
          event.event_type === "edd" &&
          event.status === "completed" &&
          event.scoring &&
          typeof event.scoring === "object"
      ) || null
    );
  }, [data?.history]);

  const surveyModel = useMemo(() => {
    if (state !== "awaiting_assessment" || !canAssess) return null;
    return buildSurveyModel(assessmentSchema);
  }, [state, canAssess, assessmentSchema]);

  const uploadFiles = useCallback(
    (sender, options) => {
      const formData = new FormData();
      options.files.forEach((file) => {
        formData.append("file", file);
        formData.append("response_id", params.id);
        formData.append("field_name", options.name);
        formData.append("file_name", file.name);
      });

      Axios.post(`${serverUrl}/api/utility/gcp/upload`, formData)
        .then((response) => {
          options.callback(
            "success",
            options.files.map((file) => ({
              file,
              name: file.name,
              type: file.type || "application/octet-stream",
              content: response.data.url,
              key: response.data.name,
              id: file.id,
            }))
          );
        })
        .catch(() => {
          options.callback("error", "File upload failed");
        });
    },
    [params.id]
  );

  useEffect(() => {
    if (!surveyModel) return undefined;
    return attachScoring(surveyModel, assessmentSchema);
  }, [surveyModel, assessmentSchema]);

  useEffect(() => {
    if (!surveyModel) return undefined;

    const completing = async (sender, options) => {
      if (isSubmittingRef.current) return;

      isSubmittingRef.current = true;
      options.allowComplete = false;
      setSubmitting(true);
      setError(null);

      try {
        const answers = formatSubmittedAnswers(sender, params.id);
        await Axios.post(`/api/edd/${params.id}/assessment`, answers);
        context?.notification?.show("EDD assessment completed.", "success", true);
        await fetchCase();
      } catch (err) {
        setError(
          err?.response?.data?.message ||
            "Unable to submit the EDD assessment right now."
        );
        isSubmittingRef.current = false;
      } finally {
        setSubmitting(false);
      }
    };

    surveyModel.onUploadFiles.add(uploadFiles);
    surveyModel.onCompleting.add(completing);

    return () => {
      surveyModel.onUploadFiles.remove(uploadFiles);
      surveyModel.onCompleting.remove(completing);
      isSubmittingRef.current = false;
    };
  }, [surveyModel, uploadFiles, params.id, fetchCase, context]);

  if (loading || submitting) return <Loader />;

  const customerName = verification
    ? [verification.first_name, verification.last_name].filter(Boolean).join(" ")
    : "";
  const navItems = caseRecord?.verification_id
    ? [
        { label: "Verification", link: `/verification/${caseRecord.verification_id}` },
        {
          label: "Risk Assessment",
          link: `/verification/risk-assessment/${caseRecord.verification_id}`,
        },
        { label: "EDD Case", link: `/verification/edd/${params.id}` },
      ]
    : [];

  return (
    <>
      {navItems.length > 0 && <SubNav items={navItems} />}
      <Card title="Enhanced Due Diligence Case">
        {error && <Message type="warning">{error}</Message>}

        {!caseRecord && !error && (
          <Message type="warning">This EDD case is unavailable.</Message>
        )}

        {caseRecord && (
          <div className="space-y-1 pb-4">
            <p>
              Customer:{" "}
              {customerName || verification?.email || "Unknown customer"}
            </p>
            {verification?.email && <p>Email: {verification.email}</p>}
            <p>State: {STATE_LABELS[state] || state || "unknown"}</p>
            {caseRecord.trigger_rating && (
              <p>Trigger rating: {caseRecord.trigger_rating}</p>
            )}
            {caseRecord.rating && <p>Assessed rating: {caseRecord.rating}</p>}
          </div>
        )}

        {state === "awaiting_customer" && (
          <Message type="info">
            Waiting for the customer to submit their Enhanced Due Diligence
            information. You can complete the internal assessment once they do.
          </Message>
        )}

        {state === "awaiting_assessment" && !canAssess && (
          <Message type="warning">
            Only EDD-capable assessors can complete the internal EDD assessment
            for this customer.
          </Message>
        )}

        {state === "awaiting_assessment" && canAssess && !assessmentSchema && (
          <Message type="warning">
            The internal EDD assessment form is not configured. Set it under
            Tranche 2 settings before assessing this case.
          </Message>
        )}

        {clientModel && (
          <div className="space-y-2 pb-4">
            <p className="text-sm font-semibold text-slate-900">
              Customer EDD submission
            </p>
            <Message type="info">
              The information the customer submitted (read-only).
            </Message>
            <div className="overflow-x-auto">
              <Survey model={clientModel} />
            </div>
          </div>
        )}

        {state === "awaiting_assessment" && canAssess && surveyModel && (
          <div className="space-y-3">
            <p className="text-sm font-semibold text-slate-900">
              Internal EDD assessment
            </p>
            <Message>
              Complete the internal EDD assessment to re-rate this customer. The
              form is fully editable until you submit it.
            </Message>
            <div className="overflow-x-auto">
              <Survey model={surveyModel} />
            </div>
          </div>
        )}

        {assessmentDisplayModel && (
          <div className="space-y-2 pb-4">
            <p className="text-sm font-semibold text-slate-900">
              Internal EDD assessment (completed)
            </p>
            <Message type="info">
              The internal assessment your team completed (read-only).
            </Message>
            <div className="overflow-x-auto">
              <Survey model={assessmentDisplayModel} />
            </div>
          </div>
        )}

        {state === "awaiting_approval" && (
          <div className="space-y-3">
            {assessmentEvent?.scoring && (
              <ScoringSummary
                scoring={assessmentEvent.scoring}
                rating={assessmentEvent.rating || caseRecord?.rating}
              />
            )}
            {canAssess ? (
              <ApprovalForm caseId={params.id} onDone={fetchCase} />
            ) : (
              <Message type="warning">
                Only EDD-capable assessors can record the approval decision.
              </Message>
            )}
          </div>
        )}

        {(state === "closed_approved" || state === "closed_declined") && (
          <Message
            type={state === "closed_approved" ? "success" : "warning"}
          >
            This EDD case is {STATE_LABELS[state].toLowerCase()}.
          </Message>
        )}

        <ReviewHistory history={data?.history} />

        {caseRecord?.verification_id && (
          <div className="pt-4">
            <Button
              text="Back to verification"
              goto={`/verification/${caseRecord.verification_id}`}
            />
          </div>
        )}
      </Card>
    </>
  );
}
